import { formatPhoneDisplay } from '@cowbell/broker/phone';
import { ActionIcon, Card, Divider, Group, Stack, Text } from '@cowbell/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEnvelope,
  faLocationDot,
  faPhone,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons';

import { REDACTED_FIELD } from '../config/logrocket';
import { useCowbellTranslations } from '../i18n/translations';

/** An agency location result, as returned by the directory search. */
export interface PinnedAgency {
  readonly id?: string;
  readonly agencyId?: string;
  readonly name?: string;
  readonly locationName?: string;
  readonly address1?: string;
  readonly city?: string;
  readonly state?: string;
  readonly zipCode?: string;
  readonly phoneNumber?: string;
}

/**
 * Agent contact for the pinned agency, when we happen to have it. The directory
 * search returns no agent — only the policyholder's own agent and the interested
 * agencies carry a named contact — so this is optional by nature.
 */
export interface PinnedAgencyContact {
  readonly agentFullName?: string;
  readonly agentEmail?: string;
  readonly agentPhone?: string;
}

interface AgencyPinPopoverProps {
  readonly agency: PinnedAgency;
  readonly contact?: PinnedAgencyContact;
  readonly onClose: () => void;
  /**
   * Consumed by google-map-react to position this child on the map; the
   * component itself never reads them.
   */
  readonly lat?: number;
  readonly lng?: number;
}

const addressOf = (agency: PinnedAgency): string | undefined => {
  const region = [agency.city, agency.state].filter(Boolean).join(', ');
  const line = [agency.address1, region, agency.zipCode]
    .filter(Boolean)
    .join(' · ');
  return line.length > 0 ? line : undefined;
};

interface RowProps {
  readonly icon: typeof faPhone;
  readonly children: React.ReactNode;
}

function Row({ icon, children }: Readonly<RowProps>) {
  return (
    <Group gap={8} wrap="nowrap" align="flex-start">
      <Text c="dimmed" span mt={2}>
        <FontAwesomeIcon icon={icon} />
      </Text>
      <Text size="sm" c="dimmed">
        {children}
      </Text>
    </Group>
  );
}

/**
 * Details for the agency behind a map pin.
 *
 * Rendered as a `google-map-react` child, so it is positioned by the `lat`/`lng`
 * props its parent passes rather than by CSS.
 */
export function AgencyPinPopover({
  agency,
  contact,
  onClose,
}: Readonly<AgencyPinPopoverProps>) {
  const { t } = useCowbellTranslations();
  const address = addressOf(agency);
  const hasContact =
    !!contact?.agentFullName || !!contact?.agentEmail || !!contact?.agentPhone;

  return (
    <Card
      withBorder
      shadow="md"
      radius="md"
      padding="sm"
      w={280}
      className={REDACTED_FIELD}
      // Sits above its pin rather than on top of it: google-map-react anchors a
      // child at its top-left corner.
      style={{ transform: 'translate(-50%, calc(-100% - 0.75rem))' }}
    >
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <Stack gap={0}>
          <Text fw={600} size="sm">
            {agency.name}
          </Text>
          {agency.locationName && (
            <Text size="xs" c="dimmed">
              {agency.locationName}
            </Text>
          )}
        </Stack>
        <ActionIcon
          variant="subtle"
          color="gray"
          size="sm"
          aria-label="Close agency details"
          onClick={onClose}
        >
          <FontAwesomeIcon icon={faXmark} />
        </ActionIcon>
      </Group>

      <Stack gap={6} mt="xs">
        {address && <Row icon={faLocationDot}>{address}</Row>}
        {agency.phoneNumber && (
          <Row icon={faPhone}>{formatPhoneDisplay(agency.phoneNumber)}</Row>
        )}
      </Stack>

      {hasContact && (
        <>
          <Divider my="xs" />
          <Text size="xs" fw={600} tt="uppercase" c="dimmed">
            {t('agent')}
          </Text>
          <Stack gap={6} mt={6}>
            {contact?.agentFullName && (
              <Text size="sm">{contact.agentFullName}</Text>
            )}
            {contact?.agentEmail && (
              <Row icon={faEnvelope}>{contact.agentEmail}</Row>
            )}
            {contact?.agentPhone && (
              <Row icon={faPhone}>{formatPhoneDisplay(contact.agentPhone)}</Row>
            )}
          </Stack>
        </>
      )}
    </Card>
  );
}

export default AgencyPinPopover;
